import { usePresenceState } from '../api/hooks/presence';
import { useT } from '../i18n/useI18n';

// Dot colours for the three presence states. Green = active (interacted in the
// last ~3 min), amber = away (logged in but idle). Offline renders nothing.
export const PRESENCE_COLORS = { active: '#16a34a', away: '#f59e0b' } as const;

/**
 * Small dot rendered next to a username/avatar to indicate presence:
 *   • green  — the user is online AND active (interacted within ~3 min)
 *   • amber  — the user is online but idle (logged in, no recent activity)
 *   • (none) — the user is offline; renders nothing so it collapses in lists
 *
 * Sizing is fixed to a tight inline pill; callers control surrounding
 * layout. The dot is accessible: aria-label reflects the state so screen
 * readers announce "online"/"away" without the visual indicator.
 */
export function PresenceDot({ userId, className }: { userId: string | null | undefined; className?: string }) {
  const state = usePresenceState(userId);
  const t = useT();
  if (state === 'offline') return null;
  const label =
    state === 'active' ? t('presence.online', 'online') : t('presence.away', 'away');
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={className}
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: PRESENCE_COLORS[state],
        // When a className is supplied the caller owns positioning (e.g. an
        // absolute overlay on an avatar), so skip the inline-pill margin.
        marginLeft: className ? undefined : 4,
        verticalAlign: 'middle',
      }}
    />
  );
}
